import { Component, OnInit, OnDestroy, Input } from '@angular/core';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { BehaviorSubject, combineLatest, Subject, Subscription } from 'rxjs';
import { SelectItem, Message } from 'primeng/api';
import { MessageService } from '@shared/services/message-service';
import { ProductService } from '../../../../services/product.service';
import { ActivityBlockingService } from '@shared/services/activity-blocking.service';
import { tap, filter } from 'rxjs/operators';
import { InvCountProductCount } from '../../../../models/inv-count-product-count';
import { BayType } from '@shared/models/bay-type';
import { InventoryService } from '../../../../services/inventory.service';
import { ShiftContextService } from '../../../../services/shift-context.service';
import { SubinventoryType } from '@shared/models/subinventory';
import { ShiftStatus } from '@shared/models/worked-shift';
import { CustomValidators } from '@shared/helpers/custom-validators';
import { ShiftContext } from '../../../../models/shift-context';
import { saveDebounce } from '@shared/helpers/save-debounce';

type ComponentsInventoryForm = FormGroup<{
    id: FormControl<number>;
    bayType: FormControl<BayType>;
    productId: FormControl<number>;
    displayOrder: FormControl<number>;
    dim1x: FormControl<number>;
    dim1y: FormControl<number>;
    pieces1: FormControl<number>;
    allocatedFromPlus: FormControl<number>;
    reclaimedFromTdwn: FormControl<number>;
    reclaimedFromSort: FormControl<number>;
}> & {
    totalInventory: number;
};

@Component({
    selector: 'app-components-inventory',
    templateUrl: './components-inventory.component.html',
    styleUrls: ['./components-inventory.component.scss']
})
export class ComponentsInventoryComponent implements OnInit, OnDestroy {

    @Input() invCountId: number;
    @Input() set subinventoryId(subinventoryId: number) {
        this.siteSubinventoryId.next(subinventoryId);
    }

    context: ShiftContext;
    isChepComponents: boolean = false;

    componentInventoryForm = new FormGroup({
        bayType: new FormControl<BayType>(null, Validators.required),
        productId: new FormControl<number>(null, Validators.required),
        dim1x: new FormControl<number>(0, Validators.required),
        dim1y: new FormControl<number>(0, Validators.required),
        pieces1: new FormControl<number>(0, Validators.required),
        allocatedFromPlus: new FormControl<number>(0, Validators.required),
        reclaimedFromTdwn: new FormControl<number>(0, Validators.required),
        reclaimedFromSort: new FormControl<number>(0, Validators.required)
    });
    bayTypes = BayType;
    bayTypesSelect: SelectItem[] = [];
    products: SelectItem[] = [];
    subscriptions: Subscription[];
    shutdown: Subject<void> = new Subject<void>();
    shifts: SelectItem[] = [];
    componentInventoryIsLoaded = false;
    componentInventoryIsSaved = false;
    siteSubinventoryId = new BehaviorSubject<number>(null);
    formTotalInventory: number;
    formDisplayOrder: number;

    componentInventoryItems: ComponentsInventoryForm[];
    autoSaveFormSubscriptions: Subscription[];

    readonly _componentInventoryItemNotCreatedMsg: Message = {
        severity: 'warn',
        summary: 'Could not Create Component Inventory Item',
        detail: 'Fields are required.'
    };

    readonly _componentInventoryItemCreatedMsg: Message = {
        severity: 'success',
        summary: 'Component Inventory Item Created',
        detail: ''
    };

    readonly _componentInventoryItemDeletedMsg: Message = {
        severity: 'warn',
        summary: 'Compoenent Inventory Item Deleted',
        detail: ''
    };
    constructor(
        private inventoryService: InventoryService,
        private productService: ProductService,
        private messageService: MessageService,
        private shiftContextService: ShiftContextService,
        private activityService: ActivityBlockingService) {
    }

    ngOnInit(): void {
        this.componentInventoryItems = [];

        const validBayTypes = [
            { value: BayType.PlantCompPlusLine, label: 'Plus' },
            { value: BayType.PlantCompPremLine, label: 'Premium' },
            { value: BayType.PlantCompCustLine, label: 'Custom' },
            { value: BayType.PlantCompMultiLine, label: 'Multicolor' },
            { value: BayType.WhiteWoodCompLine, label: 'White Wood' },
            { value: BayType.PecoCompLine, label: 'PECO' }
        ];

        const bayTypesObservable = this.productService.ComponentBayTypesForSubinventory(this.siteSubinventoryId)
            .pipe(tap(bayTypes => {
                this.bayTypesSelect = validBayTypes.filter(bt => bayTypes.some(t => t === bt.value));
            }));

        const siteObservable = this.shiftContextService.CurrentContext.pipe(tap(context => {
            this.context = context;

            this.isChepComponents = context.site.subinventories
                .find(s => s.id === this.siteSubinventoryId.value)
                ?.subinventoryType !== SubinventoryType.WhiteWoodComponents
            && context.site.subinventories
                    .find(s => s.id === this.siteSubinventoryId.value)
                    ?.subinventoryType !== SubinventoryType.PecoComponents;

            if (context.status !== ShiftStatus.Open) {
                this.componentInventoryForm.disable({ emitEvent: false });
                this.componentInventoryItems.forEach(f => f.disable({ emitEvent: false }));
            }
            else {
                this.componentInventoryForm.enable({ emitEvent: false });
                this.componentInventoryItems.forEach(f => f.enable({ emitEvent: false }));
            }
        }));

        const productsObservable = this.productService.ProductsForSubinventory(this.siteSubinventoryId)
            .pipe(tap(products => {
                this.products = products
                    .filter(p => p.productType === 'Component')
                    .map(p => ({ value: p.id, label: p.label }));
            }));

        const componentsInventoryObservable = this.inventoryService.CurrentInvProductCount.pipe(
            filter(counts => !!counts),
            tap(counts => {
                this.componentInventoryItems = counts
                    .filter(x => validBayTypes.some(bt => bt.value === x.bayType))
                    .sort((a, b) => a.displayOrder - b.displayOrder)
                    .map(bay => {
                        const form: ComponentsInventoryForm = <ComponentsInventoryForm> new FormGroup({
                            id: new FormControl<number>(bay.id),
                            bayType: new FormControl<BayType>(bay.bayType, Validators.required),
                            productId: new FormControl<number>(bay.productId, Validators.required),
                            displayOrder: new FormControl<number>(bay.displayOrder),
                            dim1x: new FormControl<number>(bay.dim1x, [Validators.required, CustomValidators.numeric(false)]),
                            dim1y: new FormControl<number>(bay.dim1y, [Validators.required, CustomValidators.numeric(false)]),
                            pieces1: new FormControl<number>(bay.pieces1, [Validators.required, CustomValidators.numeric(false)]),
                            allocatedFromPlus: new FormControl<number>(bay.allocatedFromPlus, [Validators.required, CustomValidators.numeric(false)]),
                            reclaimedFromTdwn: new FormControl<number>(bay.reclaimedFromTdwn, [Validators.required, CustomValidators.numeric(false)]),
                            reclaimedFromSort: new FormControl<number>(bay.reclaimedFromSort, [Validators.required, CustomValidators.numeric(false)])
                        });

                        if (this.context?.status !== ShiftStatus.Open)
                            form.disable({ emitEvent: false });
                        else
                            form.enable({ emitEvent: false });

                        form.totalInventory = 0;

                        this.calculateRowTotal(form);

                        return form;
                    });

                this.autoSaveFormSubscriptions?.forEach(s => s.unsubscribe());
                this.autoSaveFormSubscriptions = [
                    ...this.componentInventoryItems
                        .map(f => f.valueChanges
                            .pipe(saveDebounce(this.shutdown))
                            .subscribe(() => {
                                this.updateComponentInventory(f);
                            })),
                    ...this.componentInventoryItems
                        .map(f => f.valueChanges
                            .subscribe(() => this.calculateRowTotal(f))),
                    ...this.componentInventoryItems
                        .map(f => f.controls.bayType.valueChanges
                            .subscribe(bayType => {
                                if (bayType === BayType.PlantCompPlusLine || bayType === BayType.WhiteWoodCompLine)
                                    f.controls.allocatedFromPlus.disable({ emitEvent: false });
                                else
                                    f.controls.allocatedFromPlus.enable({ emitEvent: false });
                            }))
                ];

                this.reIndexComponent();
            }));

        this.subscriptions = [
            combineLatest([ siteObservable, productsObservable, bayTypesObservable, componentsInventoryObservable ])
                .pipe(this.activityService.Wait('Components Inventory'))
                .subscribe(([so, po, bto, cio]) => {

                    if (!this.isChepComponents) {
                        this.componentInventoryForm.controls.bayType.setValue(this.bayTypesSelect[0].value);
                        this.componentInventoryForm.controls.bayType.updateValueAndValidity({ emitEvent: false });
                        this.componentInventoryForm.controls.bayType.disable({ emitEvent: false });
                        this.onRepairTypeChange();
                    }
            }),

            combineLatest([
                this.componentInventoryForm.controls.dim1x.valueChanges,
                this.componentInventoryForm.controls.dim1y.valueChanges,
                this.componentInventoryForm.controls.pieces1.valueChanges
            ]).subscribe(() => this.calculateTotal()),

            this.componentInventoryForm.controls.bayType.valueChanges.subscribe(() => this.onRepairTypeChange())
        ];
    }

    ngOnDestroy(): void {
        this.shutdown.next();
        this.subscriptions.forEach(s => s.unsubscribe());
        this.autoSaveFormSubscriptions?.forEach(s => s.unsubscribe());
        this.autoSaveFormSubscriptions = [];
    }

    calculateTotal(): void {
        const dim1x = +this.componentInventoryForm.controls.dim1x.value || 0;
        const dim1y = +this.componentInventoryForm.controls.dim1y.value || 0;
        const pieces1 = +this.componentInventoryForm.controls.pieces1.value || 0;
        const totalValue = +dim1x * +dim1y + +pieces1;
        this.formTotalInventory = totalValue;
    }

    calculateRowTotal(componentInventoryItem: ComponentsInventoryForm): void {
        const totalValue =
            +componentInventoryItem.controls.dim1x.value * +componentInventoryItem.controls.dim1y.value
            + +componentInventoryItem.controls.pieces1.value;
        componentInventoryItem.totalInventory = totalValue;
    }

    get componentsInventoryTotal(): number {
        return (this.componentInventoryItems ?? []).reduce((sum, item) => {
            const rowTotal = (+item.controls.dim1x.value || 0) * (+item.controls.dim1y.value || 0) + (+item.controls.pieces1.value || 0);
            return sum + rowTotal;
        }, 0);
    }

    updateComponentInventory(componentInventoryItem: ComponentsInventoryForm): void {
        const item: InvCountProductCount = {
            id: componentInventoryItem.controls.id.value,
            invCountId: this.invCountId,
            bayType: componentInventoryItem.controls.bayType.value,
            productId: componentInventoryItem.controls.productId.value,
            displayOrder: componentInventoryItem.controls.displayOrder.value,
            dim1x: +componentInventoryItem.controls.dim1x.value,
            dim1y: +componentInventoryItem.controls.dim1y.value,
            pieces1: +componentInventoryItem.controls.pieces1.value,
            allocatedFromPlus: +componentInventoryItem.controls.allocatedFromPlus.value,
            reclaimedFromTdwn: +componentInventoryItem.controls.reclaimedFromTdwn.value,
            reclaimedFromSort: +componentInventoryItem.controls.reclaimedFromSort.value
        }

        this.inventoryService.updateInvCountProductCount(item).subscribe({
            next: _ => {
                this.messageService.add({
                    severity: 'success',
                    summary: 'Component Inventory Updated',
                    detail: ''
                });
            },
            error: () => {}
        });
    }

    saveCI(): void {
        if (!this.componentInventoryForm.valid) {
            this.componentInventoryForm.markAllAsTouched();
            this.messageService.add(this._componentInventoryItemNotCreatedMsg);
        } else {
            const entry: InvCountProductCount = {
                id: 0,
                bayType: this.componentInventoryForm.controls.bayType.value,
                productId: this.componentInventoryForm.controls.productId.value,
                dim1x: +this.componentInventoryForm.controls.dim1x.value,
                dim1y: +this.componentInventoryForm.controls.dim1y.value,
                pieces1: +this.componentInventoryForm.controls.pieces1.value,
                allocatedFromPlus: +this.componentInventoryForm.controls.allocatedFromPlus.value,
                reclaimedFromTdwn: +this.componentInventoryForm.controls.reclaimedFromTdwn.value,
                reclaimedFromSort: +this.componentInventoryForm.controls.reclaimedFromSort.value,
                displayOrder: this.formDisplayOrder,
                invCountId: this.invCountId
            };

            this.inventoryService.createInvCountProductCount(entry).subscribe(_ => {
                this.componentInventoryItems = [ ...this.componentInventoryItems, this.addFormGroup(_) ];
                this.componentInventoryForm.reset({
                    bayType: this.isChepComponents ? null : this.bayTypesSelect[0].value,
                    productId: null,
                    dim1x: 0,
                    dim1y: 0,
                    pieces1: 0,
                    allocatedFromPlus: 0,
                    reclaimedFromTdwn: 0,
                    reclaimedFromSort: 0
                });

                this.messageService.add(this._componentInventoryItemCreatedMsg);
            });
        }
    }

    addFormGroup(bay: InvCountProductCount): ComponentsInventoryForm {
        const form = <ComponentsInventoryForm> new FormGroup({
            id: new FormControl<number>(bay.id),
            bayType: new FormControl<BayType>(bay.bayType, Validators.required),
            productId: new FormControl<number>(bay.productId, Validators.required),
            displayOrder: new FormControl<number>(bay.displayOrder),
            dim1x: new FormControl<number>(bay.dim1x, [Validators.required, CustomValidators.numeric(false)]),
            dim1y: new FormControl<number>(bay.dim1y, [Validators.required, CustomValidators.numeric(false)]),
            pieces1: new FormControl<number>(bay.pieces1, [Validators.required, CustomValidators.numeric(false)]),
            allocatedFromPlus: new FormControl<number>(bay.allocatedFromPlus, [Validators.required, CustomValidators.numeric(false)]),
            reclaimedFromTdwn: new FormControl<number>(bay.reclaimedFromTdwn, [Validators.required, CustomValidators.numeric(false)]),
            reclaimedFromSort: new FormControl<number>(bay.reclaimedFromSort, [Validators.required, CustomValidators.numeric(false)])
        });

        this.calculateRowTotal(form);

        this.autoSaveFormSubscriptions.push(
            form.valueChanges
                .pipe(saveDebounce(this.shutdown))
                .subscribe(() => {
                    this.updateComponentInventory(form);
                }),
            form.valueChanges
                .subscribe(() => this.calculateRowTotal(form)),
            form.controls.bayType.valueChanges
                .subscribe(bayType => {
                    if (bayType === BayType.PlantCompPlusLine || bayType === BayType.WhiteWoodCompLine)
                        form.controls.allocatedFromPlus.disable({ emitEvent: false });
                    else
                    form.controls.allocatedFromPlus.enable({ emitEvent: false });
                }));

        return form;
}

    deleteInventoryRow(componentInventoryItem: ComponentsInventoryForm): void {
        this.inventoryService.deleteInvCountProductCount(componentInventoryItem.controls.id.value).subscribe(() => {
            this.componentInventoryItems.splice(this.componentInventoryItems.indexOf(componentInventoryItem), 1);
            this.messageService.add(this._componentInventoryItemDeletedMsg);
            this.reIndexComponent();
        });
    }

    reIndexComponent(): void {
        let componentIndex = 1;
        if (this.componentInventoryItems) {
            for (const componentInventoryItem of this.componentInventoryItems.sort((l, r) =>
                    l.controls.displayOrder.value - r.controls.displayOrder.value)) {
                componentInventoryItem.controls.displayOrder.setValue(componentIndex, { emitEvent: false, onlySelf: true });
                componentIndex++;
            }
        }

        this.formDisplayOrder = componentIndex;
    }

    rowTrackBy(_i: number, item: ComponentsInventoryForm): number {
        return item.controls.id.value;
    }

    onRepairTypeChange(): void {
        const allocatedFromPlus = this.componentInventoryForm.controls.allocatedFromPlus;
        if (!this.isAllocBayType()) {
            if (+allocatedFromPlus.value !== 0)
                allocatedFromPlus.setValue(0);

            allocatedFromPlus.disable({ emitEvent: false });
        }
        else {
            allocatedFromPlus.enable({ emitEvent: false });
        }
    }
    isAllocBayType(): boolean {
        const value = this.componentInventoryForm.controls.bayType.value;
        return this.isChepComponents && !(value === BayType.PlantCompPlusLine || value === BayType.WhiteWoodCompLine);
    }
}
